// @flow
import React, { useEffect } from 'react';
import { featureIsEnabled } from '@ta/common.features';
import SavesCarouselAnimation from '@ta/memx.saves-educator';
import { LoadingSpinner } from '@ta/onboarding.common';
import { useLocalize, UnsafeLocalize } from '@ta/platform.i18n';
import { useGARecorder } from '@ta/platform.interactions';
import Header from './Header';
import styles from './EndScreen.css';

type Props = {|
  displayName: ?string,
  destinationGeoName: ?string,
  willRedirectToNearby: boolean,
  isEducatorEligible: boolean,
|};

/**
 * A loading animation displayed while we're saving the user's selections and reloading the page
 */
const EndScreen = ({
  displayName,
  destinationGeoName,
  willRedirectToNearby,
  isEducatorEligible,
}: Props) => {
  const { localize } = useLocalize();
  const isSavesEducator =
    featureIsEnabled('saves_educator_explicit_preferences') && isEducatorEligible;
  const isSavesEducatorControl = featureIsEnabled('saves_educator_control') && isEducatorEligible;
  const recordAction = useGARecorder();

  let titleText;
  if (displayName) {
    // Thanks, {displayName}
    titleText = localize('cx_explicit_preferences_profile_end_screen2', { displayName });
  } else {
    // Thanks.
    titleText = localize('cx_explicit_preferences_profile_end_screen_no_name');
  }

  let subtitleText;
  if (isSavesEducator) {
    // Now let&#39;s help you plan an awesome trip. When you see something you like, tap the heart to save it.
    subtitleText = <UnsafeLocalize id="ONB_saves_educator_tap_the_heart_subhead" />;
  } else if (willRedirectToNearby) {
    // Now start discovering nearby ideas from other travelers.
    subtitleText = localize('cx_explicit_preferences_end_body_nearby');
  } else if (destinationGeoName != null) {
    // Now start discovering great {destinationGeoName} ideas from other travelers like you.
    subtitleText = localize('cx_explicit_preferences_end_body_geo', { destinationGeoName });
  }

  useEffect(() => {
    if (isSavesEducator) {
      recordAction({
        module: 'saves_educator',
        action: 'saves_educator_end_screen',
      });
    } else if (isSavesEducatorControl) {
      recordAction({
        module: 'saves_educator',
        action: 'saves_educator_control',
      });
    }
  }, [recordAction, isSavesEducator, isSavesEducatorControl]);

  return (
    <div className={styles.endScreen}>
      <div className={styles.contents}>
        <Header titleText={titleText} subtitleText={subtitleText} />
        {isSavesEducator ? (
          <div className={styles.savesEducator}>
            {' '}
            <SavesCarouselAnimation />{' '}
          </div>
        ) : (
          <LoadingSpinner />
        )}
      </div>
    </div>
  );
};

export default EndScreen;
